import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BedDouble, UtensilsCrossed, Camera, Landmark, Train, Plane, Ship, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import PageHero from "@/components/PageHero";
import { Container, ShoreBadge, AdvertBadge } from "@/components/shared";

const CAT_META = {
  stay: { label: "Places to stay", icon: <BedDouble size={18} /> },
  eat: { label: "Places to eat", icon: <UtensilsCrossed size={18} /> },
  see: { label: "Places to see", icon: <Camera size={18} /> },
  site: { label: "Historically connected sites", icon: <Landmark size={18} /> },
};

const SHORES = [
  { key: "all", label: "Both shores" },
  { key: "lincolnshire", label: "Lincolnshire" },
  { key: "massachusetts", label: "Massachusetts" },
];

export default function Tourism() {
  const [data, setData] = useState(null);
  const [shore, setShore] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/tourism", { params: { shore } }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [shore]);

  return (
    <div>
      <PageHero
        kicker="Tourism · Two Bostons, one journey"
        title="Visit both shores"
        intro="Where to stay, where to eat, what to see, and the historically connected sites that bind the two Bostons. Subscribed businesses sit at the top of each list; free listings share five rotating slots each month."
      >
        <div className="flex flex-wrap gap-2" data-testid="tourism-shore-filters">
          {SHORES.map((s) => (
            <button key={s.key} data-testid={`tourism-shore-${s.key}`} onClick={() => setShore(s.key)}
              className={`font-sans text-sm px-4 py-2 border transition-colors ${shore === s.key ? "bg-[#1E1E2F] text-[#FFFAF3] border-[#1E1E2F]" : "bg-[#FFFAF3] text-[#1B2430] border-[#D6D3CB] hover:border-[#CDA434]"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </PageHero>

      {/* How to get here — the crossing link */}
      <section className="py-14 bg-[#1E1E2F] text-[#FFFAF3] border-b border-[#CDA434]">
        <Container>
          <div className="flex items-center gap-2 text-[#CDA434] mb-6">
            <span className="font-sans text-xs uppercase tracking-[0.2em]">How to get here · The crossing</span>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <TravelCard icon={<Train size={20} />} title="London → Boston, Lincolnshire" body="Rail from London King's Cross to Grantham or Peterborough, then a connecting service to Boston. By road, roughly two and a half hours up the A1 and A17." />
            <TravelCard icon={<Plane size={20} />} title="Arriving in Boston, Massachusetts" body="Logan International connects the daughter city to the world, with subway and water-taxi links into the harbour and Common." />
            <TravelCard icon={<Ship size={20} />} title="Crossing between the two" body="Fly transatlantic into Boston Logan or London, then follow the routes above. A full crossing guide is on the way." />
          </div>
          <p className="font-sans text-xs text-[#FFFAF3]/50 mt-6">Detailed travel guides are being written. This section is a placeholder for now.</p>
        </Container>
      </section>

      <section className="py-12 sm:py-16">
        <Container>
          {loading || !data ? (
            <div className="space-y-16">
              {[...Array(2)].map((_, i) => <div key={i} className="h-64 bg-[#FFFAF3] border border-[#D6D3CB] animate-pulse" />)}
            </div>
          ) : (
            <div className="space-y-20">
              {Object.keys(CAT_META).map((cat) => {
                const block = data[cat] || { featured: [], rotating: [] };
                const all = [...block.featured, ...block.rotating];
                return (
                  <div key={cat} data-testid={`tourism-cat-${cat}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="w-10 h-10 border border-[#CDA434] flex items-center justify-center text-[#CDA434]">{CAT_META[cat].icon}</span>
                      <h2 className="font-serif text-2xl text-[#003366]">{CAT_META[cat].label}</h2>
                    </div>
                    <p className="font-sans text-xs text-[#505A68] mb-6 ml-13 pl-13" style={{ paddingLeft: "3.25rem" }}>
                      {block.free_total > 5
                        ? `${block.featured.length} subscribed · showing 5 of ${block.free_total} free listings this month (${data._month})`
                        : `${all.length} listing${all.length === 1 ? "" : "s"}`}
                    </p>

                    {block.featured.length > 0 && (
                      <>
                        <div className="flex items-center gap-3 mb-4">
                          <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#CDA434] font-semibold">Subscribed</span>
                          <AdvertBadge label="Paid listing" />
                          <div className="flex-1 h-px bg-[#CDA434]/40" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                          {block.featured.map((t) => <TourismCard key={t.id} t={t} featured />)}
                        </div>
                      </>
                    )}

                    {block.rotating.length > 0 && (
                      <>
                        <div className="flex items-center gap-3 mb-4">
                          <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#505A68] font-semibold">This month's free listings</span>
                          <div className="flex-1 h-px bg-[#D6D3CB]" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {block.rotating.map((t) => <TourismCard key={t.id} t={t} />)}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Container>
      </section>

      <section className="pb-20">
        <Container>
          <div className="bg-[#FFFAF3] border border-[#D6D3CB] p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-serif text-xl text-[#1E1E2F]">Run a hotel, restaurant, or attraction?</h3>
              <p className="font-sans text-sm text-[#505A68] mt-1">List in the tourism space. Subscribe to sit at the top all year, or take a rotating free slot.</p>
            </div>
            <Link to="/join" className="inline-flex items-center bg-[#CDA434] hover:bg-[#b8912b] text-[#1E1E2F] font-sans font-semibold text-sm px-6 py-3 whitespace-nowrap">List your business</Link>
          </div>
        </Container>
      </section>
    </div>
  );
}

function TravelCard({ icon, title, body }) {
  return (
    <div className="border border-[#FFFAF3]/15 bg-[#161622] p-6">
      <div className="text-[#CDA434] mb-3">{icon}</div>
      <h3 className="font-serif text-base text-[#FFFAF3] mb-2">{title}</h3>
      <p className="font-sans text-sm text-[#FFFAF3]/70 leading-relaxed">{body}</p>
    </div>
  );
}

function TourismCard({ t, featured }) {
  return (
    <div data-testid={`tourism-card-${t.id}`} className={`group flex flex-col bg-[#FFFAF3] overflow-hidden transition-colors ${featured ? "border-2 border-[#CDA434]" : "border border-[#D6D3CB] hover:border-[#CDA434]"}`}>
      <div className="relative aspect-[4/3] overflow-hidden bg-[#F8F1E7]">
        <img src={t.image_url} alt={t.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <span className="absolute top-3 left-3"><ShoreBadge shore={t.shore} /></span>
        {featured && <span className="absolute top-3 right-3 bg-[#CDA434] text-[#1E1E2F] text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5">Subscribed</span>}
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-serif text-lg text-[#1E1E2F] leading-snug">{t.name}</h3>
        <p className="font-sans text-xs uppercase tracking-wide text-[#505A68] mt-1">{t.town}</p>
        <p className="font-sans text-sm text-[#505A68] leading-relaxed mt-3 flex-1">{t.description}</p>
        {t.website && (
          <a href={t.website} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 font-sans text-sm font-semibold text-[#003366]">
            Visit website <ArrowUpRight size={14} />
          </a>
        )}
      </div>
    </div>
  );
}
